import { defineComponent as _defineComponent } from 'vue'
import { AppDirection } from '@/enums/app-direction';
import { useMetaStore } from '@/helpers/composables/useStores';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { usePointerSwipe } from '@vueuse/core';
import type { SwiperInstance } from 'tiny-swiper';

import { computed, onActivated, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxFreeTrialSwiperOSContainer',
  props: {
    itemCount: null,
    delay: null,
    initialSlide: { default: 0 },
    persistedSlideIndex: null,
    fullLine: { type: Boolean }
  },
  emits: ['slideChange'],
  setup(__props: any, { expose, emit: emits }) {

const props = __props as { itemCount: number, delay: number, initialSlide: number, persistedSlideIndex: number, fullLine?: boolean };

let swiper: SwiperInstance | null = null;
const currentSlideIndex = ref(0);
const itemContainer = ref<HTMLElement | null>(null);



const { appDirection } = useMetaStore();

if (!IS_TOUCH) {
	const { isSwiping } = usePointerSwipe(itemContainer);
	// Ignore click events *while* swiping on desktop
	watch(isSwiping, isCurrentlySwiping => {
		if (!isCurrentlySwiping) return;

		itemContainer.value?.addEventListener(
			'click',
			e => {
				e.preventDefault();
				e.stopPropagation();
			},
			{ once: true, capture: true }
		);
	});
}

const isRTL = computed(() => appDirection.value === AppDirection.RTL);
const trackNonInteractionSwipe = ref(props.initialSlide > 0);

onMounted(() => {
	initSwiper();
	window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', handleResize);
});

onActivated(initSwiper);

function next(allowRTL: boolean = false) {
	if (isRTL.value && !allowRTL) {
		prev(true);
		return;
	}
	if (currentSlideIndex.value === props.itemCount - 1) {
		swiper?.slideTo(0);
	} else {
		swiper?.slideTo(currentSlideIndex.value + 1);
	}
}

function prev(allowRTL: boolean = false) {
	if (isRTL.value && !allowRTL) {
		next(true);
		return;
	}
	if (currentSlideIndex.value === 0) {
		swiper?.slideTo(props.itemCount - 1);
	} else {
		swiper?.slideTo(currentSlideIndex.value - 1);
	}
}
function changeSlideIndex(index: number, nextIndex: number): void {
	currentSlideIndex.value = nextIndex;
	if (index !== null && nextIndex !== index) {
		emits('slideChange', {
			direction: index < nextIndex ? 'right' : 'left',
			position: nextIndex,
			nonInteraction: trackNonInteractionSwipe.value,
		});
	}
	if (trackNonInteractionSwipe.value) {
		trackNonInteractionSwipe.value = false;
	}
}

function initSwiper(persistedSlideIndex: number = 0) {
	try {
		// swiper has a top level global variable calling `requestAnimationFrame` so the import needs to be client only
		// Because of a watcher in the tree, this function should not be async
		import('tiny-swiper').then(({ default: Swiper }) => {
			if (itemContainer.value && process.client) {
				if (swiper == null) {
					swiper = new Swiper(itemContainer.value, {
						// initialSlide: persistedSlideIndex,
						loop: false,
					});
				}

				itemContainer.value.querySelectorAll('.swiper-slide').forEach(el => el.classList.remove('flex-100'));

				if (persistedSlideIndex === 0 && props.initialSlide > 0) {
					setTimeout(() => {
						swiper?.slideTo(props.initialSlide);
						emits('slideChange', { direction: 'right', position: props.initialSlide, skipTracking: true });
						currentSlideIndex.value = props.initialSlide;
					}, props.delay);
				} else {
					currentSlideIndex.value = persistedSlideIndex;
				}

				swiper.on('before-slide', (current: number, state: any, next: number) =>
					changeSlideIndex(current, next)
				);
			}
		});
	} catch (error: any) {
		captureMessageForSentry('[Initialize Tiny Swiper]', { error, where: '[TinySwiper]: initSwiper' }, 'error');
	}
}

function handleResize() {
	if (swiper?.update) {
		swiper.update();
	}
}

expose({
	next,
	prev,
	currentSlideIndex,
});

return { __sfc: true,swiper, currentSlideIndex, itemContainer, props, emits, appDirection, isRTL, trackNonInteractionSwipe, next, prev, changeSlideIndex, initSwiper, handleResize, faChevronLeft, faChevronRight }
}

})